import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { components } from 'react-select';
import Tippy from '@tippy.js/react';
import { GeneralButton } from 'sharedComponents/GeneralButton';
import { GeneralSelect } from 'sharedComponents/GeneralSelect';
import { ToggleButton } from 'sharedComponents/ToggleButton';
import { IconCondition, IconTrash, IconWarning } from 'svgIcons/MotionPortalIcons';
import { LEVEL, LIMIT_SYMBOL } from './constants';
import { getLimitValueRegex } from './helper';
import { translate } from 'helpers/translateHelper';
import { useDispatch } from 'react-redux';
import { deleteLimit, updateLimit } from '../actions';
import { Switch } from 'sharedComponents/SwitchComponent';
import colors from 'theme/_colors.scss';

export const options = [
    { label: 'Alarm', value: 'Alarm', prefixIcon: <IconCondition condition={3} />, prefixIconSelected: <IconCondition condition={3} /> },
    { label: 'Alert', value: 'Alert', prefixIcon: <IconCondition condition={2} />, prefixIconSelected: <IconCondition condition={2} /> }
];

const LimitSelectSingleValue = (props) => {
    const tooltip = props?.data?.tooltip;

    const content =
        <components.SingleValue {...props}>
            {props.children}
        </components.SingleValue>;

    if (!tooltip) { return content; }

    return (
        <Tippy
            content={tooltip}
            animation='scale'
            theme='light-border'
            placement='top'
            duration={200}
            offset='0,2'
            appendTo={() => document.body}
        >
            <span>{content}</span>
        </Tippy>
    );
};

const LimitSelectOption = (optionProps) => {
    const { data, children } = optionProps;
    const tooltip = data?.tooltip;

    return (
        <components.Option {...optionProps}>
            <Tippy
                content={tooltip}
                animation='scale'
                theme='light-border'
                placement='top'
                duration={200}
                offset='0,2'
            >
                <span className='option-label option-label--with-tooltip'>{children}</span>
            </Tippy>
        </components.Option>
    );
};

const limitSelectComponents = {
    Option: LimitSelectOption,
    SingleValue: LimitSelectSingleValue
};

const NotifyMeComponent = ({ item, handleUpdateLimit, translate }) => {
    return <div className='d-flex align-items-center'>
        <Switch className='me-2' checked={item?.notificationEnabled} onChange={(value) => { handleUpdateLimit(item?.uniqId, 'notificationEnabled', value); }} />{translate('ABB.Powertrain.Frontend.limitsNotifyMe')}
    </div>;
};

const Limit = ({ item, timeSeries, checkIsSmseAsset }) => {
    const dispatch = useDispatch();
    const [focusedInput, setFocusedInput] = useState(null);
    const limitValueRegex = getLimitValueRegex(item?.precision ?? timeSeries?.timeseriesPrecision ?? timeSeries?.precision);

    const handleDeleteLimit = (item) => {
        dispatch(deleteLimit(item));
    };

    const handleUpdateLimit = (uniqId, field, value) => {
        dispatch(updateLimit(uniqId, field, value, timeSeries?.uniqId));
    };
    const handleFocusInput = (val) => {
        setFocusedInput(val);
    };
    const isDefaultLimit = item?.isDefault && !item?.isBasedOnDefault;
    const showDescriptionInput = checkIsSmseAsset
        ? true
        : !isDefaultLimit;
    const descriptionValue = item?.description || '';
    const isDisabledLimit = checkIsSmseAsset ? !(timeSeries?.isDefaultLimitEditable || timeSeries?.isAdditionalLimitAllowed || timeSeries?.isAddingCustomLimitsAllowed) : false; // Enabling the default Limits to be Edited
    const descriptionInputDisabled = checkIsSmseAsset ? false : isDisabledLimit;
    const toggleButtonOptions = isDisabledLimit ? options.filter((op) => item?.limitLevel === op.label) : options;
    const limitValueError = item?.limitValue === '' || item?.limitValue === null || item?.limitValue === undefined;
    return (
        <div className='limit-box'>
            <div className='limit-values-container'>
                {!item?.isUserLimit &&
                    <div className='d-flex align-items-center'>
                        <Tippy
                            content={item?.eventEnabled ?
                                translate('ABB.Powertrain.Frontend.eventEnabledLimitConfiguration') :
                                translate('ABB.Powertrain.Frontend.eventDisabledLimitConfiguration')}
                            animation='scale'
                            theme='light-border'
                            placement='top'
                            hideOnClick={false}
                            duration={100}
                        >
                            <div>
                                <Switch className='me-2' checked={item?.eventEnabled} onChange={(value) => { handleUpdateLimit(item?.uniqId, 'eventEnabled', value); }} />
                            </div>
                        </Tippy>
                    </div>}
                <div className='limit-left-values'>
                    <div className={`limit-level-container ${isDisabledLimit ? 'disabled' : ''}`}>
                        <ToggleButton
                            options={toggleButtonOptions}
                            selectedValue={isDisabledLimit ? '' : item?.limitLevel}
                            onSelected={(val) => handleUpdateLimit(item?.uniqId, 'limitLevel', val)}
                            buttonProps={{
                                disabled: isDisabledLimit
                            }}
                        />
                    </div>
                    <div className='limit-value-fields'>
                        <GeneralSelect
                            className='limit-select-tooltip'
                            value={
                                item?.limitDirection
                                    ? {
                                        label:
                                            item?.limitDirection === LEVEL.UpperLimit ||
                                                item?.limitDirection === LEVEL.Upper
                                                ? LIMIT_SYMBOL.Greater
                                                : LIMIT_SYMBOL.Lower,
                                        value: item?.limitDirection,
                                        tooltip:
                                            item?.limitDirection === LEVEL.UpperLimit
                                                ? translate('ABB.Powertrain.Frontend.upperLimit')
                                                : translate('ABB.Powertrain.Frontend.lowerLimit')
                                    }
                                    : { label: LIMIT_SYMBOL.Default, value: '', isDisabled: true }
                            }
                            options={
                                [
                                    { label: '>', value: LEVEL.UpperLimit, tooltip: translate('ABB.Powertrain.Frontend.upperLimit') },
                                    { label: '<', value: LEVEL.LowerLimit, tooltip: translate('ABB.Powertrain.Frontend.lowerLimit') }
                                ]
                            }
                            onChange={(e) => {
                                handleUpdateLimit(item?.uniqId, 'limitDirection', e.value);
                            }}
                            components={limitSelectComponents}
                            isDisabled={isDisabledLimit}
                            isSearchable={false}
                            showErrorIndicator={false}
                            error={!item?.limitDirection}
                        />
                        <div
                            className={`limit-value-uom ${isDisabledLimit ? 'disabled' : ''} ${timeSeries?.minValue && timeSeries?.maxValue && (item?.limitValue < timeSeries?.minValue || item?.limitValue > timeSeries?.maxValue) || item?.isSameLimit || limitValueError ? 'error' : ''} ${focusedInput === 'limitValue' ? 'highlight' : ''}`}>
                            <input
                                type='number'
                                placeholder={translate('ABB.Powertrain.Frontend.inputValue')}
                                value={item?.limitValue === 0 ? '0' : item?.limitValue || ''}
                                onFocus={() => { handleFocusInput('limitValue'); }}
                                onBlur={() => { handleFocusInput(null); }}
                                onChange={(e) => {
                                    const value = e.target.value;

                                    if (limitValueRegex.test(value)) {
                                        handleUpdateLimit(item?.uniqId, 'limitValue', value);
                                    }
                                }}
                                name='limitValue'
                                disabled={isDisabledLimit}
                            />
                            {item?.isSameLimit && <IconWarning color={colors.red} width={16} height={16} />}
                            {timeSeries?.unit && <div className='uom-text'>{timeSeries?.unit}</div>}
                        </div>
                        <div className='ms-3 limit-label'>
                            {showDescriptionInput ?
                                <div className={`limit-value-uom ${focusedInput === 'description' ? 'highlight' : ''}`}>
                                    <input
                                        type='text'
                                        value={descriptionValue}
                                        placeholder='Input description'
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            handleUpdateLimit(item?.uniqId, 'description', value);
                                        }}
                                        name='description'
                                        disabled={descriptionInputDisabled}
                                        onFocus={() => { handleFocusInput('description'); }}
                                        onBlur={() => { handleFocusInput(false); }}
                                    />
                                </div> :
                                item?.description ? <div>{item.description}</div> : null}
                        </div>
                    </div>
                </div>
                <NotifyMeComponent item={item} handleUpdateLimit={handleUpdateLimit} translate={translate} />
                <div className='limit-right-values'>
                    {!item?.isDefault && !item?.isBasedOnDefault &&
                        <GeneralButton
                            className='limit-delete-button'
                            icon={<IconTrash width={15}
                                height={18}
                                style={{ marginRight: 0 }} />}
                            onClick={() => handleDeleteLimit(item)}>
                        </GeneralButton>
                    }
                </div>
            </div>
        </div>
    );
};

// Prop validation for NotifyMeComponent
NotifyMeComponent.propTypes = {
    item: PropTypes.shape({
        uniqId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Unique identifier for the limit
        notificationEnabled: PropTypes.bool, // Whether notifications are enabled
    }),
    handleUpdateLimit: PropTypes.func, // Function to handle limit updates
    translate: PropTypes.func, // Translation function
};

// Prop validation for Limit component
Limit.propTypes = {
    item: PropTypes.shape({
        uniqId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Unique identifier for the limit
        precision: PropTypes.number, // Precision for limit value validation
        isUserLimit: PropTypes.bool, // Whether the limit is a user-defined limit
        eventEnabled: PropTypes.bool, // Whether the event is enabled
        limitLevel: PropTypes.string, // The level of the limit (e.g., 'Alarm', 'Alert')
        limitDirection: PropTypes.string, // Direction of the limit ('UpperLimit' or 'LowerLimit')
        limitValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), // Value of the limit
        description: PropTypes.string, // Description for the limit
        isDefault: PropTypes.bool, // Whether the limit is a default limit
        isBasedOnDefault: PropTypes.bool, // Whether the limit is based on a default limit
        isModified: PropTypes.bool, // Whether the limit has unsaved user changes
        isSameLimit: PropTypes.bool, // Whether the limit is the same as another limit
    }),
    timeSeries: PropTypes.shape({
        uniqId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Unique identifier for the time series
        isDefaultLimitEditable: PropTypes.bool, // Whether the default limit is editable
        minValue: PropTypes.number, // Minimum value for the limit
        maxValue: PropTypes.number, // Maximum value for the limit
        precision: PropTypes.number, // Legacy precision for numeric validation
        timeseriesPrecision: PropTypes.number, // Timeseries precision for numeric validation
        unit: PropTypes.string, // Unit of measurement for the limit
        displayName: PropTypes.string, // Display name for the time series
        trendKey: PropTypes.string, // Trend key for the time series
    }),
    checkIsSmseAsset: PropTypes.bool, // Whether the asset is an SMSE asset
};

export default React.memo(Limit);
